import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { useEffect, useMemo, useRef, useState } from 'react';
import interactionPlugin from '@fullcalendar/interaction';

import './calendar.css';
import { cn } from '@/lib/utils';
import { LeaveEvent } from './LeaveEvent';
import { useTranslation } from 'react-i18next';
import { Legend, type LegendItem } from './Legend';
import { LeaveCalendarHeader } from './LeaveCalendarHeader';
import { LeaveTimeline, type TimelineEmployee } from './LeaveTimeline';
import type { CalendarEvent } from '@/types/leave-calendar';

/**
 * Props for the LeaveCalendar component
 */
interface LeaveCalendarProps {
  className?: string;
  selectedYear?: number;
  legends?: LegendItem[];
  selectedMonth?: number;
  filters?: React.ReactNode;
  events?: CalendarEvent[];
  nonWorkingDays?: number[];
  /** `YYYY-MM` keys that have leave — the month picker dots these. */
  monthsWithLeave?: string[];
  /** Right-aligned summary in the footer legend, e.g. how many people are away. */
  legendTrailing?: React.ReactNode;
  /** `month` is the grid; `team` is the per-employee timeline. */
  view?: 'month' | 'team';
  /** Under the team view's last row (a load-more control for a paged roster). */
  teamFooter?: React.ReactNode;
  /** Roster for the team view — everyone here keeps a row, leave or not. */
  employees?: TimelineEmployee[];
  onDateChange?: (dateEvent: {
    month: number;
    year: number;
    date: Date;
  }) => void;
}

const today = new Date();

/**
 * A calendar component that displays a month view of the calendar.
 *
 * @param filters - A React Node that will be rendered above the calendar header.
 * @param className - The className of the container element.
 * @param events - An array of CalendarEvent objects that will be rendered on the calendar.
 * @param legends - An array of LegendItem objects that will be rendered below the calendar.
 * @param nonWorkingDays - An array of day indices that are non working days.
 * @param onDateChange - A function that will be called when the date changes.
 * @param selectedMonth - The currently selected month.
 * @param selectedYear - The currently selected year.
 */
export function LeaveCalendar({
  filters,
  className,
  events = [],
  legends = [],
  nonWorkingDays = [],
  monthsWithLeave,
  legendTrailing,
  view = 'month',
  employees,
  teamFooter,
  onDateChange = () => {},
  selectedMonth = today.getMonth(),
  selectedYear = today.getFullYear(),
}: LeaveCalendarProps) {
  const calendarRef = useRef<FullCalendar>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { i18n } = useTranslation();
  const [date, setDate] = useState(new Date(selectedYear, selectedMonth));
  useEffect(() => {
    setDate(new Date(selectedYear, selectedMonth));
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    onDateChange({
      month: date.getMonth(),
      year: date.getFullYear(),
      date: date,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Resize observer
  useEffect(() => {
    if (!containerRef.current || !calendarRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      calendarRef.current?.getApi().updateSize();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  /**
   * FullCalendar's own `height="100%"` needs its ancestors to resolve a
   * percentage, which silently yields zero often enough that the grid just
   * disappears. Measuring the slot and handing over a number sidesteps that
   * entirely. `auto` until the first measurement, so it renders either way.
   */
  const gridSlotRef = useRef<HTMLDivElement>(null);
  const [gridHeight, setGridHeight] = useState<number | 'auto'>('auto');

  useEffect(() => {
    const el = gridSlotRef.current;
    if (!el || view !== 'month') return;

    const measure = () => {
      // Rounded so sub-pixel jitter doesn't re-render on every observation.
      const h = Math.round(el.getBoundingClientRect().height);
      // Ignore a collapsed slot — better to keep rendering at natural height
      // than to hand FullCalendar a zero and show nothing.
      setGridHeight(h > 1 ? h : 'auto');
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [view]);

  // Sync Calendar API with State
  useEffect(() => {
    if (calendarRef.current) {
      calendarRef.current.getApi().gotoDate(date);
    }
  }, [date]);

  const getNonWorkingClasses = (dayIndex: number, cssClass: string) => {
    return nonWorkingDays.includes(dayIndex) ? [cssClass] : [];
  };

  // Local parts, not toISOString — a UTC conversion shifts the date either side
  // of midnight and shades the neighbouring day instead.
  const toLocalIso = (d: Date) => {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };

  const holidayDates = useMemo(() => {
    const dates = new Set<string>();
    events.forEach((ev) => {
      if (ev.extendedProps.type === 'holiday') {
        dates.add(ev.start.slice(0, 10));
      }
    });
    return dates;
  }, [events]);

  return (
    <div
      ref={containerRef}
      className={cn('flex flex-col gap-4 relative', className)}
    >
      <LeaveCalendarHeader
        filters={filters}
        selectedDate={date}
        onDateChange={setDate}
        monthsWithLeave={monthsWithLeave}
      />

      <div className="leave-calendar-view">
        {view === 'team' ? (
          <LeaveTimeline
            events={events}
            nonWorkingDays={nonWorkingDays}
            selectedMonth={date.getMonth()}
            selectedYear={date.getFullYear()}
            employees={employees}
            footer={teamFooter}
          />
        ) : (
          <div className="leave-calendar-month" ref={gridSlotRef}>
            <div className="leave-calendar-month-inner">
              <FullCalendar
                firstDay={1}
                height={gridHeight}
                events={events}
                dayMaxEvents={5}
                aspectRatio={1.8}
                ref={calendarRef}
                initialDate={date}
                headerToolbar={false}
                fixedWeekCount={false}
                locale={i18n.language}
                initialView="dayGridMonth"
                plugins={[dayGridPlugin, interactionPlugin]}
                moreLinkContent={(args) => `+${args.num} more`}
                eventContent={(eventInfo) => (
                  <LeaveEvent eventInfo={eventInfo} />
                )}
                dayCellClassNames={({ date }) => [
                  ...getNonWorkingClasses(
                    date.getDay(),
                    'non-working-day-cell'
                  ),
                  ...(holidayDates.has(toLocalIso(date))
                    ? ['holiday-day-cell']
                    : []),
                ]}
                dayHeaderClassNames={({ dow }) =>
                  getNonWorkingClasses(dow, 'non-working-day-cell')
                }
              />
            </div>
          </div>
        )}
      </div>

      <Legend legendItems={legends} trailing={legendTrailing} />
    </div>
  );
}
